
import { defineComponent, SetupContext, getCurrentInstance, onUnmounted, computed, onMounted, ref, reactive } from 'vue';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import cookies from 'js-cookie';
import Popup from 'vant/lib/popup';
import FooterNav from '@/components/footer-nav/index.vue';
import Loading from '@/components/loading/index.vue';
import { radarLog, sendLog } from '@/common/radar';
import { genWebDidAndSessionId } from '@/common/utils';
import { launchApp, initDeviceFingerprint, injectTabStrategyToAppIfNeeded } from '@/common/layout-common';
import { ComponentNameMap, FirebaseConfig } from '@/constants/base';
import { ReportFirebaseTokenRequest, UserApiService } from '@/api/user';
import { ABTestApiService } from '@/api/ab-test';
import PwaSharePopup from '@/components/pwa-share-popup/index.vue';
import AppReferralPromptPopup from '~/components/web-to-app-popup/app-referral-popup.vue';
import device from '@/common/device-info';
import { getKpn } from '@/common/common-params';
import { filmRedirect } from '@/common/client_redirect';
import { useWebToApp, OpenPageStatus } from '~/hooks/useWebToApp';
import { buildIDsSystem } from '@/common/ids-mapping-sys';
import { tokenCollectedCallApp } from '@/common/call-app';
import ActionOpenPwa from '@/components/action-open-pwa/index.vue';
import { installPwaFun, isContinue } from '@/common/pwa';

const firebaseApp = initializeApp(FirebaseConfig);

const LightThemePageList = ['discover-content', 'playlist-detail', 'profile', 'topic-content', 'account-management', 'article-content'];

const IconMap = {
    PushIcon: 'https://cdn-static.ap4r.com/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/push-icon.png',
    SharePopupTopIcon: 'https://aws-static.ap4r.com/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/share-popup-top-icon.png',
    KwaiIcon: 'https://cdn-static.ap4r.com/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/kwai-icon.png',
    SnackIcon: 'https://cdn-static.ap4r.com/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/snack-icon.png',
};

/**
 * @zh 通知权限状态
 */
enum OpenStatus {
    UnSupported = 0, // 不支持通知：1、系统不支持 2、firebase 不支持(如防火墙限制等)
    Close = 1, // 未开启
    Opend = 2, // 已开启
    Opening = 3, // 本次开启
    Closing = 4, // 本次关闭
    MaybeLater = 5,
    EnableNow = 6,
}

const PopupType = {
    installPwa: 'installPwa',
    openPwa: 'openPwa',
};

export default defineComponent({
    components: {
        FooterNav,
        Loading,
        PwaSharePopup,
        AppReferralPromptPopup,
        ActionOpenPwa,
        Popup,
    },
    setup(_: unknown, ctx: SetupContext) {
        const { proxy }: any = getCurrentInstance() || {};
        const startupData: any = proxy.$store.state.startup;
        const UserApi = new UserApiService();
        const ABTestApi = new ABTestApiService();
        const showBtn = ref(true);
        const { isIOS = false } = device();

        const popupState: Record<string, any> = reactive({
            isFirstOpen: false,
            showOpenPwaBtn: false,
            type: PopupType.openPwa,
        });
        const visible = ref(false);
        const popup_background = 'https://cdn-static.kwai.net/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/popup-background.png';
        const images = [
            'https://cdn-static.kwai.net/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/popup-avator-1.png',
            'https://cdn-static.kwai.net/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/popup-avator-2.png',
            'https://cdn-static.kwai.net/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/popup-avator-3.png',
        ];

        const showFooter = computed(() => {
            return proxy?.$route?.path === '/old-foryou' || proxy?.$route?.path === '/';
        });

        const { webToAppPopupVisible, handleWebToAppPopupConfirm, handleWebToAppPopupClose, checkIfInstalled, installPwa } = useWebToApp();

        /**
         * @zh 通知弹窗实验
         * @zh 实验链接 https://abtest-sgp.corp.kuaishou.com/abnew#/experiment/10625/group?docType=12
         */
        const isABTestExpGroup = computed(() => {
            if (typeof window?.Notification?.permission === 'undefined' || window?.Notification?.permission === 'granted') {
                return false;
            }
            return proxy?.$store?.state?.abTestModule?.abTestParams?.notification_check === 'true' && !proxy?.$isAppEnvironment;
        });

        const showNotificationPopup = computed(() => {
            const isOpend = window.Notification?.permission === 'granted';
            return !isOpend && isABTestExpGroup.value && proxy?.$store?.state?.showNotificationPopup && !isIOS;
        });

        const appName = computed(() => {
            const name = (getCurrentInstance()?.proxy as { $appName?: string })?.$appName;
            return name || '';
        });

        const loading = ref(false);

        const setLoading = (isLoading: boolean) => {
            loading.value = isLoading;
        };

        const getNotificationABTest = async () => {
            try {
                const res = await ABTestApi.getABTestConfig({
                    url: window.location.pathname,
                    parameter: 'notification_check',
                });
                const { parameter, value } = res?.data || {};
                if (!parameter) {
                    return;
                }
                proxy?.$store?.commit?.('abTestModule/setABTestParams', {
                    [parameter]: value,
                });
                return value === 'true';
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e);
            }
        };

        const getCurrentPermissionStatus = async (): Promise<OpenStatus> => {
            const isSupport = await isSupported();

            if (!isSupport) {
                radarLog({
                    name: 'Unsupport_Firebase',
                    category: 'KWAI_SHARE',
                    event_type: 'click',
                });
            }

            if (typeof window?.Notification?.permission === 'undefined' || !isSupport) {
                return OpenStatus.UnSupported;
            }

            return window?.Notification?.permission === 'granted' ? OpenStatus.Opend : OpenStatus.Close;
        };

        const reportToken = async (token: string, extraParams = {}) => {
            console.log('token: ', token);
            const bucket = startupData.bucket || cookies.get('bucket');
            const kpn = getKpn({ bucket, host: window.location.host });

            const data: ReportFirebaseTokenRequest = {
                push_token: token,
                timestamp: Date.now(),
                domain: window.location.origin,
                url: window.location.href,
                ua: window.navigator.userAgent,
                /**
                 * @en `default_web_did_` as default prefix
                 * @en can be analyzed by the prefix
                 */
                web_did: cookies.get('webDid') || `default_web_did_${Date.now()}`,
                kwai_id: proxy?.$store?.state?.userModule?.userInfo?.user?.kwai_id,
                user_id_str: cookies.get('user_id'),
                session_id: cookies.get('sessionId'),
                countryInfo: startupData.countryInfo || cookies.get('countryInfo'),
                bucket,
                kpn,
                extra_params: { ...extraParams },
            };

            if (!token) {
                radarLog({
                    name: 'Get_Firebase_Token_Error',
                    category: 'KWAI_SHARE',
                    event_type: 'click',
                    extra_info: JSON.stringify(data),
                });
                return;
            }

            await UserApi.reportFirebaseToken(data);
        };

        const sendNotificationPermissionLog = (status: OpenStatus) => {
            sendLog({
                type: 'click',
                name: 'PWA_AUTHORITY',
                value: {
                    status,
                },
            });
        };

        const registerSW = async () => {
            try {
                const registration = await navigator.serviceWorker.register(`${window?.location?.origin}/firebase-messaging-sw.js`, {
                    scope: '/firebase-cloud-messaging-push-scope',
                });

                return !!registration;
            } catch {
                return false;
            }
        };

        const registerFirebaseMessaging = async () => {
            try {
                const isSupport = await isSupported();
                console.log('isSupport: ', isSupport);
                if (!window?.navigator?.serviceWorker || !isSupport) {
                    return;
                }

                /**
                 * @zh 在注册 firebase 前先判断是否已经开启
                 */
                const hasOpened = window.Notification?.permission === 'granted';
                const isRegisterSW = await registerSW();
                if (!isRegisterSW) {
                    return;
                }
                const message = getMessaging(firebaseApp);
                const token = await getToken(message);
                await reportToken(token);

                if (!hasOpened) {
                    /**
                     * @zh 之前 “未开启” 才上报 “本次开启”
                     */
                    sendNotificationPermissionLog(OpenStatus.Opening);
                }
            } catch (e) {
                if (window.Notification?.permission === 'default') {
                    // 用户手动拒绝
                    sendNotificationPermissionLog(OpenStatus.Closing);
                }
            }
        };

        const handleNotificationPopupOk = async () => {
            proxy?.$store?.commit?.('setShowNotificationPopup', false);
            sendNotificationPermissionLog(OpenStatus.EnableNow);
            await registerFirebaseMessaging();
        };

        const handleNotificationPopupCancel = () => {
            sendNotificationPermissionLog(OpenStatus.MaybeLater);
            proxy?.$store?.commit?.('setShowNotificationPopup', false);
        };

        const changeShow = (isShow: boolean) => {
            popupState.showOpenPwaBtn = isShow;
        };

        // 展示 使用pwa打开弹窗
        const openWithPwaDiadog = () => {
            changeShow(true);
            popupState.type = PopupType.openPwa;
        };

        let timeInstallPwaId: any = null;
        const pwaGlobalClick = () => {
            installPwaFun(installPwa, 'agree_interaction');
        };
        // pwa 实验逻辑
        const pwaExp = () => {
            // 开启pwa实验
            const pwaStatus = checkIfInstalled();
            console.log('pwaStatus', pwaStatus);
            // 已安装 非pwa 模式 & 一天一次
            if (pwaStatus === OpenPageStatus.hasPwaInWeb) {
                isContinue('openPwa').then(res => {
                    if (res) {
                        changeShow(true);
                        popupState.type = PopupType.openPwa;
                    }
                });
            } else if (pwaStatus === OpenPageStatus.noPwaInWeb) {
                // 未安装，重置频控
                localStorage.setItem(
                    'pwaData',
                    JSON.stringify({
                        install: '',
                        openPwa: '',
                    })
                );

                // 消费同意
                if (startupData.open_guide_seconds) {
                    timeInstallPwaId = setTimeout(() => {
                        console.log('自动弹窗逻辑');
                        installPwaFun(installPwa, 'agree_consume').finally(() => {
                            // 如果没有触发定时弹窗。那么走点击互动逻辑
                            window.addEventListener('click', pwaGlobalClick);
                        });
                    }, Number(startupData.open_guide_seconds) * 1000);
                } else {
                    // 全局监听点击事件
                    window.addEventListener('click', pwaGlobalClick);
                }
            }
        };
        const handleCancel = (source: string) => {
            visible.value = false;
            if (source === 'close') {
                sendLog({
                    type: 'click',
                    name: 'KWAI_LITE_POP',
                    value: {
                        click_area: 'cancel',
                    },
                });
            }
        };

        const openGooglePlay = () => {
            const url = 'https://klite.onelink.me/NMJa/tflosamr';
            window.open(url);
            sendLog({
                type: 'click',
                name: 'KWAI_LITE_POP',
                value: {
                    click_area: 'try',
                },
            });
        };

        const checkPopupConditions = () => {
            const { isIOS } = device() || {};
            if (!isIOS) {
                const url = window?.location?.href;
                const params = new URLSearchParams(new URL(url).search);
                const KWAI_LITE_SHARE = 'kwai_lite_share';
                const KWAI_LITE_VIDEO = 'kwai.lite.video';
                const pwa_source = params.get('pwa_source');
                const app_package_name = params.get('app_package_name');
                const lastPopupTime = localStorage.getItem('kwai_lite_lastPopupTime');
                if (pwa_source === KWAI_LITE_SHARE && app_package_name === KWAI_LITE_VIDEO) {
                    const currentTime = Date.now();
                    if (lastPopupTime) {
                        const lastTime = parseInt(lastPopupTime);
                        const hoursDifference = (currentTime - lastTime) / (1000 * 60 * 60);
                        // 大于24h
                        if (hoursDifference > 24) {
                            visible.value = true;
                            localStorage.setItem('kwai_lite_lastPopupTime', currentTime.toString());
                            sendLog({
                                type: 'show',
                                name: 'KWAI_LITE_POP',
                            });
                        }
                    } else {
                        // 第一次弹窗
                        localStorage.setItem('kwai_lite_lastPopupTime', currentTime.toString());
                        visible.value = true;
                        sendLog({
                            type: 'show',
                            name: 'KWAI_LITE_POP',
                        });
                    }
                }
            }
        };

        // 安装完成回调
        const onappinstalledCb = () => {
            const { openPwaafterInstall } = proxy?.$store?.state?.abTestModule?.abTestParams || {};
            setTimeout(() => {
                changeShow(true);
                popupState.type = PopupType.openPwa;
                popupState.isFirstOpen = true;
            }, openPwaafterInstall * 1000);
        };

        onMounted(async () => {
            genWebDidAndSessionId();
            initDeviceFingerprint();
            injectTabStrategyToAppIfNeeded();

            const bucket = proxy?.$store?.state?.startup?.bucket;
            // launchApp调用时获取不到this 无法拿到bucket值
            sessionStorage.setItem('bucket', bucket);
            // 全局注册拉起方法
            (proxy as Record<string, any>)?.$bus?.on?.('launchApp', launchApp);
            (proxy as Record<string, any>)?.$bus?.on?.('isLoad', setLoading);

            (proxy as Record<string, any>)?.$bus?.on?.('openWithPwaDiadog', openWithPwaDiadog);

            const res = await UserApi.getUserInfo({});
            if (res?.result === 1 && !!res?.user) {
                proxy?.$store?.commit?.('userModule/setUserInfo', {
                    user: res.user,
                });
            }

            const isABTestGroup = await getNotificationABTest();
            if (isABTestGroup) {
                registerFirebaseMessaging();
                const status = await getCurrentPermissionStatus();
                sendNotificationPermissionLog(status);
            }
            filmRedirect(proxy, true);
            buildIDsSystem(proxy, true);

            // 开启 pwa 逻辑
            if (startupData.if_open_guide_to_desktop && !isIOS && bucket === 'br') {
                //
                pwaExp();

                // 监听pwa 安装完成，需要延迟 5s
                window.onappinstalled = onappinstalledCb;
            }
            checkPopupConditions();
        });

        const bgColor = computed(() => {
            const isLightTheme = LightThemePageList.includes(proxy?.$route?.name || '');
            if (isLightTheme) {
                return '#fff';
            }
            return 'black';
        });

        onUnmounted(() => {
            (proxy as Record<string, any>)?.$bus?.off?.('launchApp', launchApp);
            (proxy as Record<string, any>)?.$bus?.off?.('isLoad', setLoading);
            (proxy as Record<string, any>)?.$bus?.off?.('openWithPwaDiadog', openWithPwaDiadog);
            timeInstallPwaId && clearTimeout(timeInstallPwaId);
            window.removeEventListener('click', pwaGlobalClick);
        });

        return {
            bgColor,
            showFooter,
            loading,
            showNotificationPopup,
            appName,
            IconMap,
            ComponentNameMap,
            webToAppPopupVisible,

            handleNotificationPopupOk,
            handleNotificationPopupCancel,
            handleWebToAppPopupConfirm,
            handleWebToAppPopupClose,
            showBtn,

            popupState,
            changeShow,

            visible,
            popup_background,
            images,
            handleCancel,
            openGooglePlay,
        };
    },
    head() {
        // const i18nHead = this.$nuxtI18nHead({ addSeoAttributes: true })
        const host = (this as any).$host;
        const languagemap = this.$i18n.localeCodes.map((el: string) => {
            return {
                rel: 'alternate',
                hreflang: el,
                href: `https://${host + decodeURI(this.$route.path)}?lang=${el}`,
            };
        });

        return {
            link: [
                {
                    rel: 'canonical',
                    href: `https://${host + decodeURI(this.$route.path)}`,
                },
                ...languagemap,
            ],
        };
    },
    // watch: {
    // eslint-disable-next-line func-names
    // '$route.path': function (newValue, oldValue, ...args) {
    //     console.log(newValue, oldValue, '----args----');
    //     try {
    //         const { $isAppEnvironment } = this?.$nuxt?.context || {};
    //         if (newValue !== oldValue && $isAppEnvironment) {
    //             window.$yoda?.invoke?.('webview.open', {
    //                 url: `${window?.location?.origin}${newValue}`,
    //             });
    //         }
    //     } catch (e) {
    //         console.log(e);
    //     }
    // },
    // },
});
